import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, Output, EventEmitter } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Attachment } from '@shared/entities/attachment';
import { LearningAttachment } from '@shared/entities/learningAttachment';
import { LearningAttachmentService } from '@shared/services/api/learning-attachment.service';
import { FsSignedUrlService } from '@shared/services/api/fs-signed-url.service';
import { GenerateSignedUrlResponse } from '@shared/entities/generateSignedUrlResponse';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-learning-attachment-input',
  templateUrl: './learning-attachment-input.component.html',
  styleUrl: './learning-attachment-input.component.scss'
})
export class LearningAttachmentInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() learningId = '';
  @Input() titleKey = 'FILE_INPUT_HISTORY.VIDEO';
  @Input() noFilesKey = 'FILE_INPUT_HISTORY.NO_VIDEOS_UPLOADED';
  @Input() accept = 'video/*';
  @Input() filterByAccept = true;
  @Input() downloadProperty = 'attachmentId';
  @Input() mediaType: 'video' | 'image' | 'document' = 'video';

  @Output() pathsChanged = new EventEmitter<string[]>();

  attachments: Attachment[] = [];
  uploadedPaths: string[] = [];
  isUploading = false;
  uploadProgress = 0;
  uploadError: string | null = null;
  removingIds = new Set<string>();
  removeError: string | null = null;
  private isInitialized = false;
  private destroy$ = new Subject<void>();

  constructor(
    private _learningAttachmentService: LearningAttachmentService,
    private fsSignedUrlService: FsSignedUrlService,
    private http: HttpClient,
    public translateService: TranslateService
  ) {}

  ngOnInit(): void {
    if (!this.isInitialized && this.learningId) {
      this.loadAttachments();
      this.isInitialized = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['learningId'] && !changes['learningId'].firstChange && this.learningId) {
      this.loadAttachments();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUploadedPaths(): string[] {
    return [...this.uploadedPaths];
  }

  private loadAttachments(): void {
    if (!this.learningId) {
      return;
    }

    this._learningAttachmentService.getAttachment(this.learningId, this.mediaType).subscribe({
      next: (learningAttachments) => {
        this.updateAttachmentsFromResponse(learningAttachments);
      },
      error: () => {
        this.attachments = [];
      }
    });
  }

  private updateAttachmentsFromResponse(learningAttachments: LearningAttachment[]): void {
    this.attachments = [];

    if (!learningAttachments || learningAttachments.length === 0) {
      this.uploadedPaths = [];
      this.emitPathsChanged();
      return;
    }

    const allAttachments: Attachment[] = learningAttachments.map((item) => item as any as Attachment);
    this.attachments = this.filterAttachmentsByMediaType(allAttachments);
    this.uploadedPaths = this.attachments.map((att) => att.filePath || att.id).filter((path): path is string => !!path);
    this.emitPathsChanged();
  }

  private filterAttachmentsByMediaType(attachments: Attachment[]): Attachment[] {
    if (!attachments || attachments.length === 0) {
      return [];
    }

    const filtered = attachments.filter((attachment) => {
      if (!attachment.contentType) {
        return false;
      }

      let matches = false;

      if (this.mediaType === 'video') {
        matches = attachment.contentType.startsWith('video/');
      } else if (this.mediaType === 'image') {
        matches = attachment.contentType.startsWith('image/');
      } else if (this.mediaType === 'document') {
        matches =
          attachment.contentType === 'application/pdf' ||
          attachment.contentType === 'application/msword' ||
          attachment.contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      }

      return matches;
    });

    return filtered;
  }

  onUpload = (file: File) => {
    if (!file) {
      this.uploadError = this.translateService.instant('FILE_INPUT_HISTORY.NO_FILE_SELECTED');
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadError = null;

    this.uploadToStorage(file);
  };

  private uploadToStorage(file: File): void {
    const payload: any = {
      fileName: file.name
    };

    if (file.type) {
      payload.contentType = file.type;
    }

    this.fsSignedUrlService
      .generateSignedUrl(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (signedUrlResponse: GenerateSignedUrlResponse) => {
          this.uploadFileToSignedUrl(file, signedUrlResponse);
        },
        error: (err) => {
          this.uploadError =
            typeof err === 'string' ? err : this.translateService.instant('FILE_INPUT_HISTORY.URL_GENERATION_ERROR');
          this.isUploading = false;
        }
      });
  }

  private uploadFileToSignedUrl(file: File, signedUrlResponse: GenerateSignedUrlResponse): void {
    if (!signedUrlResponse || !signedUrlResponse.url) {
      this.uploadError = this.translateService.instant('FILE_INPUT_HISTORY.URL_NOT_AVAILABLE');
      this.isUploading = false;
      return;
    }

    const headers = new HttpHeaders({});

    this.http
      .put(signedUrlResponse.url, file, {
        headers,
        reportProgress: true,
        observe: 'events'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadProgress = Math.round((100 * event.loaded) / event.total);
          } else if (event.type === HttpEventType.Response) {
            this.uploadProgress = 100;
            const uploadedPath = this.extractPathFromSignedUrl(signedUrlResponse.url);

            this.uploadedPaths.push(uploadedPath);

            const newAttachment = new Attachment();
            newAttachment.id = uploadedPath;
            newAttachment.filePath = uploadedPath;
            newAttachment.fileName = file.name;
            newAttachment.contentType = file.type;

            this.attachments.push(newAttachment);

            setTimeout(() => {
              this.isUploading = false;
              this.uploadProgress = 0;
            }, 500);

            this.emitPathsChanged();
          }
        },
        error: () => {
          this.uploadError = this.translateService.instant('FILE_INPUT_HISTORY.UPLOAD_FAILED');
          this.isUploading = false;
          this.uploadProgress = 0;
        }
      });
  }

  private extractPathFromSignedUrl(signedUrl: string): string {
    try {
      const url = new URL(signedUrl);
      const pathname = url.pathname;
      const parts = pathname.split('/').filter((p) => p);
      if (parts.length > 1) {
        return parts.slice(1).join('/');
      }
      return pathname;
    } catch {
      return signedUrl;
    }
  }

  private emitPathsChanged(): void {
    this.pathsChanged.emit([...this.uploadedPaths]);
  }

  onRemove = (attachmentId: string) => {
    const attIndex = this.attachments.findIndex((att: any) => {
      if (att.attachmentId === attachmentId) return true;
      if (att.filePath === attachmentId || att.id === attachmentId) return true;
      return false;
    });

    if (attIndex === -1) {
      this.emitPathsChanged();
      return;
    }

    const removedAttachment = this.attachments[attIndex] as any;
    const backendAttachmentId: string | undefined = removedAttachment.attachmentId;

    if (backendAttachmentId && this.learningId) {
      this.removingIds = new Set(this.removingIds);
      this.removingIds.add(backendAttachmentId);
      this.removeError = null;

      this._learningAttachmentService
        .deleteAttachment(this.learningId, backendAttachmentId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this._removeFromLocalState(attIndex, removedAttachment, backendAttachmentId);
          },
          error: () => {
            this.removingIds = new Set(this.removingIds);
            this.removingIds.delete(backendAttachmentId);
            this.removeError = this.translateService.instant('FILE_INPUT_HISTORY.DELETE_ERROR');
          }
        });
    } else {
      this._removeFromLocalState(attIndex, removedAttachment, undefined);
    }
  };

  private _removeFromLocalState(attIndex: number, removedAttachment: any, backendAttachmentId: string | undefined): void {
    const pathToRemove = removedAttachment.filePath || removedAttachment.id;
    if (pathToRemove) {
      const pathIndex = this.uploadedPaths.indexOf(pathToRemove);
      if (pathIndex > -1) {
        this.uploadedPaths.splice(pathIndex, 1);
      }
    }
    this.attachments.splice(attIndex, 1);
    if (backendAttachmentId) {
      this.removingIds = new Set(this.removingIds);
      this.removingIds.delete(backendAttachmentId);
    }
    this.emitPathsChanged();
  }

  onPathsChanged(paths: string[]): void {
    this.uploadedPaths = [...paths];
    this.emitPathsChanged();
  }

  isRemoving(attachmentId: string): boolean {
    return this.removingIds.has(attachmentId);
  }
}
